import { measureMintStage } from './ordinal-mint-diagnostics'
import {
    blobMintContentSource,
    hashMintContentSource,
    type MintContentSource,
    type OrdinalMintIntent,
} from './ordinal-local-mint-signing'

const INTENT_PREFIX = 'treechat-ordinal-mint-intent:'
const INTENT_TTL_MS = 30 * 60 * 1000
const runtimeFiles = new Map<string, File>()
const runtimeIntents = new Map<string, OrdinalMintIntent>()

export type OrdinalMintMediaInfo = {
    answer_id: string
    attachment_id: string
    blob_id: string
    byte_size: number
    content_type: string
    filename?: string
    payment_address: string
    ordinals_address: string
    service_fee_address: string
    service_fee_satoshis: number
    service_fee_quote: string
    app: string
}

export type MintChunkFetcher = (
    answerId: string,
    attachmentId: string,
    offset: number,
    length: number,
) => Promise<Uint8Array>

function intentKey(answerId: string, attachmentId: string): string {
    return `${INTENT_PREFIX}${answerId}:${attachmentId}`
}

function runtimeKey(answerId: string, attachmentId: string): string {
    return `${answerId}:${attachmentId}`
}

function validIntent(intent: OrdinalMintIntent): boolean {
    return (
        intent.version === 1 &&
        Boolean(intent.userId && intent.deviceId && intent.answerId && intent.attachmentId) &&
        Boolean(intent.paymentAddress && intent.ordinalAddress && intent.contentType) &&
        /^[0-9a-f]{64}$/i.test(intent.contentSha256) &&
        Number.isSafeInteger(intent.contentBytes) &&
        intent.contentBytes > 0 &&
        Number.isSafeInteger(intent.serviceFeeSatoshis) &&
        intent.serviceFeeSatoshis > 0 &&
        Number.isSafeInteger(intent.maxNetworkFeeSatoshis) &&
        intent.maxNetworkFeeSatoshis > 0 &&
        Number.isSafeInteger(intent.createdAt) &&
        intent.createdAt <= Date.now() &&
        Date.now() - intent.createdAt <= INTENT_TTL_MS
    )
}

export function saveOrdinalMintIntent(intent: OrdinalMintIntent, originalFile?: File): void {
    if (!validIntent(intent)) throw new Error('Invalid ordinal mint intent')
    const key = runtimeKey(intent.answerId, intent.attachmentId)
    runtimeIntents.set(key, intent)
    try {
        localStorage.setItem(
            intentKey(intent.answerId, intent.attachmentId),
            JSON.stringify(intent),
        )
    } catch {
        // Keep the current mobile page able to sign when persistent storage is restricted.
    }
    if (originalFile)
        runtimeFiles.set(runtimeKey(intent.answerId, intent.attachmentId), originalFile)
}

export function getOrdinalMintIntent(
    answerId: string,
    attachmentId?: string,
): OrdinalMintIntent | null {
    if (attachmentId) {
        const runtime = runtimeIntents.get(runtimeKey(answerId, attachmentId))
        if (runtime && validIntent(runtime)) return runtime
        let serialized: string | null = null
        try {
            serialized = localStorage.getItem(intentKey(answerId, attachmentId))
        } catch {
            return null
        }
        if (!serialized) return null
        try {
            const intent = JSON.parse(serialized) as OrdinalMintIntent
            if (
                !validIntent(intent) ||
                intent.answerId !== answerId ||
                intent.attachmentId !== attachmentId
            ) {
                clearOrdinalMintIntent(answerId, attachmentId)
                return null
            }
            return intent
        } catch {
            clearOrdinalMintIntent(answerId, attachmentId)
            return null
        }
    }

    for (const intent of runtimeIntents.values()) {
        if (intent.answerId === answerId && validIntent(intent)) return intent
    }
    try {
        for (let index = 0; index < localStorage.length; index++) {
            const key = localStorage.key(index)
            if (!key?.startsWith(`${INTENT_PREFIX}${answerId}:`)) continue
            const candidateAttachment = key.slice(`${INTENT_PREFIX}${answerId}:`.length)
            const intent = getOrdinalMintIntent(answerId, candidateAttachment)
            if (intent) return intent
        }
    } catch {
        return null
    }
    return null
}

export function clearOrdinalMintIntent(answerId: string, attachmentId: string): void {
    runtimeIntents.delete(runtimeKey(answerId, attachmentId))
    try {
        localStorage.removeItem(intentKey(answerId, attachmentId))
    } catch {}
    runtimeFiles.delete(runtimeKey(answerId, attachmentId))
}

function remoteContentSource(
    intent: OrdinalMintIntent,
    fetchChunk: MintChunkFetcher,
): MintContentSource {
    return {
        mode: 'remote_chunks',
        size: intent.contentBytes,
        type: intent.contentType,
        name: intent.filename,
        readChunk: (start, end) =>
            fetchChunk(intent.answerId, intent.attachmentId, start, end - start),
    }
}

export function contentSourceForOrdinalMintIntent(
    intent: OrdinalMintIntent,
    fetchChunk: MintChunkFetcher,
): MintContentSource {
    const originalFile = runtimeFiles.get(runtimeKey(intent.answerId, intent.attachmentId))
    if (
        originalFile &&
        originalFile.size === intent.contentBytes &&
        (!originalFile.type || originalFile.type === intent.contentType) &&
        (!intent.filename || originalFile.name === intent.filename)
    ) {
        return blobMintContentSource(originalFile, originalFile.name)
    }
    return remoteContentSource(intent, fetchChunk)
}

export async function prepareOrdinalMintIntent({
    userId,
    deviceId,
    media,
    serviceFeeSatoshis,
    maxNetworkFeeSatoshis,
    fetchChunk,
    originalFile,
}: {
    userId: string
    deviceId: string
    media: OrdinalMintMediaInfo
    serviceFeeSatoshis: number
    maxNetworkFeeSatoshis: number
    fetchChunk: MintChunkFetcher
    originalFile?: File
}): Promise<OrdinalMintIntent> {
    if (
        !media.answer_id ||
        !media.attachment_id ||
        !media.blob_id ||
        !media.payment_address ||
        !media.ordinals_address ||
        !media.service_fee_address ||
        !media.app ||
        !Number.isSafeInteger(media.byte_size) ||
        media.byte_size <= 0
    ) {
        throw new Error('Mint media cannot be safely reacquired')
    }

    const provisional: OrdinalMintIntent = {
        version: 1,
        userId,
        deviceId,
        answerId: media.answer_id,
        attachmentId: media.attachment_id,
        blobId: media.blob_id,
        paymentAddress: media.payment_address,
        ordinalAddress: media.ordinals_address,
        contentType: media.content_type,
        contentBytes: media.byte_size,
        contentSha256: '0'.repeat(64),
        app: media.app,
        filename: media.filename,
        serviceFeeAddress: media.service_fee_address,
        serviceFeeSatoshis,
        maxNetworkFeeSatoshis,
        createdAt: Date.now(),
    }
    if (originalFile)
        runtimeFiles.set(runtimeKey(media.answer_id, media.attachment_id), originalFile)
    const source = contentSourceForOrdinalMintIntent(provisional, fetchChunk)
    const contentSha256 = await measureMintStage(
        {
            answer_id: media.answer_id,
            attachment_id: media.attachment_id,
            content_bytes: source.size,
            source: source.mode,
        },
        'intent_hash',
        observe => hashMintContentSource(source, observe),
    )
    const intent = { ...provisional, contentSha256 }
    saveOrdinalMintIntent(intent, originalFile)
    return intent
}
